import React from 'react';
import cookie from 'cookie';
import uuidv4 from 'uuid/v4';
import ProductRow from '../components_common/ProductRow';
import { getTopLevelDomainOf } from './routeHelpers';
import Country from '../common/models/Country';

const CLIENT_UUID_COOKIE_NAME = 'italistClientUUID';

const getCookie = () => cookie.parse(document.cookie);

const getCookieValue = key => getCookie()[key];

const setCookieValue = (key, value, options) => {
  document.cookie = cookie.serialize(key, value, options);
};

const setClientIdCookieIfEmpty = domain => {
  if (!getCookieValue(CLIENT_UUID_COOKIE_NAME)) {
    const distantFuture = new Date();
    distantFuture.setFullYear(new Date().getFullYear() + 30);
    const topLevelDomain = `.${getTopLevelDomainOf(domain)}`;
    setCookieValue(CLIENT_UUID_COOKIE_NAME, uuidv4(), { expires: distantFuture, domain: topLevelDomain, path: '/' });
  }
};

const alphabet = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
  'num'
];

const requiresCookieBanner = (isEu, euCookiePolicyAccepted) => {
  return isEu && !euCookiePolicyAccepted;
};

const animateScroll = (container, destination, duration) => {
  if (duration > 0) {
    const difference = destination - container.scrollTop;
    const step = (difference / duration) * 10;

    setTimeout(() => {
      container.scrollTop += step;
      if (container.scrollTop !== destination) {
        animateScroll(container, destination, duration - 10);
      }
    }, 5);
  }
};

const defaultPageSize = 60;

const sortOptionPVInsertTime = 'insert_time';
const sortOptionInsertTimePriceDesc = 'price_desc';
const sortOptionInsertTimePriceAsc = 'price_asc';
const onSaleParam = 'on_sale=1';
const newInParam = `sort_field=${sortOptionPVInsertTime}`;

const sortOptions = [
  // {label: 'Sort By', value: sortOptionInsertTime},
  { label: 'New Arrivals', value: sortOptionPVInsertTime },
  { label: 'Price (Highest First)', value: sortOptionInsertTimePriceDesc },
  { label: 'Price (Lowest First)', value: sortOptionInsertTimePriceAsc }
];

const formatNum = input => parseFloat(input).toFixed(2);

const genRandomStr = () => (Math.random() + 3.14159265359).toString(36).substring(2);

const generateProductRows = (productRows, s3BasePath, currencySymbol, isDesktop, showSale, rowsToLoadEagerlyCount, placeholderHeight, placeholderWidth) => {
  return productRows.map((productRow, idx) => (
    <ProductRow
      currencySymbol={currencySymbol}
      key={`product-rows-${genRandomStr()}`}
      isDesktop={isDesktop}
      productRow={productRow}
      s3BasePath={s3BasePath}
      showSale={showSale}
      lazyLoad={rowsToLoadEagerlyCount ? idx >= rowsToLoadEagerlyCount : null}
      placeholderWidth={placeholderWidth}
      placeholderHeight={placeholderHeight}
    />
  ));
};

const generateRowImages = rows => {
  const rowData = {};
  for (const [key, value] of Object.entries(rows)) {
    switch (key) {
      case '1':
        rowData.row1 = [...value.images];
        break;
      case '2':
        rowData.row6 = [...value.images];
        break;
      default:
        continue;
    }
  }
  return rowData;
};

const separateProductDataIntoRows = (products, rowLength) => {
  let groups = [];

  return products.reduce((acc, product, index) => {
    groups.push(product);
    if (groups.length === rowLength || index === products.length - 1) {
      acc.push(groups);
      groups = [];
    }
    return acc;
  }, []);
};

// Products are delivered in multiples of 60 from the backend. Skips are restricted
// to multiples of 60 even if the user manually changes the skip in the URL
const roundDownToMultipleOf60 = skip => {
  if (isNaN(skip)) {
    return 0;
  }
  return skip % 60 === 0 ? skip : roundDownToMultipleOf60(skip - 1);
};

const getPrevSkipAmount = skip => {
  return skip - 60 > 0 ? skip - 60 : 0;
};

const getNextSkipAmount = (skip, total) => {
  return skip + 60 < total ? skip + 60 : 0;
};

const isNumber = num => {
  return !isNaN(Number(num));
};

const tag = ([raw]) => {
  const [labelBgColor, labelColor, label, ...message] = raw.split(' ');
  const tagCss = `
    color: ${labelColor};
    background-color: ${labelBgColor};
    font-weight: bold;
    padding: 0 .5em 0 .5em
  `;
  return [`%c${label}:%c ${message.join(' ')}`, tagCss, ''];
};

const callbackFnWithTimeout = (callback, time = 1000) => {
  let called = false;
  const fn = () => {
    if (!called) {
      called = true;
      callback();
    }
  };
  setTimeout(fn, time);
  return fn;
};

const getCreditCardType = number => {
  let re = new RegExp('^(4026|417500|4508|4844|491(3|7))');
  if (number.match(re) !== null) {
    return 'Visa Electron';
  }

  re = new RegExp('^4');
  if (number.match(re) !== null) {
    return 'Visa';
  }

  re = new RegExp(
    '^(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))$'
  );
  if (re.test(number)) {
    return 'Mastercard';
  }

  re = new RegExp('^3[47]');
  if (number.match(re) !== null) {
    return 'AMEX';
  }

  re = new RegExp('^(6011|622(12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[0-1][0-9]|92[0-5]|64[4-9])|65)');
  if (number.match(re) !== null) {
    return 'Discover';
  }

  re = new RegExp('^36');
  if (number.match(re) !== null) {
    return 'Diners';
  }

  re = new RegExp('^30[0-5]');
  if (number.match(re) !== null) {
    return 'Diners - Carte Blanche';
  }

  re = new RegExp('^35(2[89]|[3-8][0-9])');
  if (number.match(re) !== null) {
    return 'JCB';
  }

  return '';
};

const invalidCcNumCheck = (num, isUsd) => {
  num = num.replace(/\s/g, '');
  if (!isNumber(num) || num.length < 14) {
    return true;
  }

  const cardType = getCreditCardType(num);
  let supportedCards = ['Visa', 'Visa Electron', 'Mastercard'];
  if (isUsd) {
    supportedCards = [...supportedCards, 'AMEX', 'JCB'];
  }

  return !supportedCards.includes(cardType);
};

const shouldUseUnbounce = countryId => {
  return !Country.regionalPopupCountries().includes(countryId.toString());
};

const replaceSpaceWithDash = str => str.replace(/\s/g, '-');

export {
  alphabet,
  animateScroll,
  defaultPageSize,
  invalidCcNumCheck,
  formatNum,
  generateProductRows,
  generateRowImages,
  genRandomStr,
  getCookie,
  getCookieValue,
  getCreditCardType,
  getNextSkipAmount,
  getPrevSkipAmount,
  isNumber,
  replaceSpaceWithDash,
  requiresCookieBanner,
  roundDownToMultipleOf60,
  separateProductDataIntoRows,
  setClientIdCookieIfEmpty,
  setCookieValue,
  onSaleParam,
  newInParam,
  shouldUseUnbounce,
  sortOptions,
  sortOptionPVInsertTime,
  sortOptionInsertTimePriceDesc,
  sortOptionInsertTimePriceAsc,
  tag
};
